import React, { useEffect, useState, useCallback } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { PageHeader } from '../components/PageHeader';
import { Plus, Edit2, Trash2, FileSignature } from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { formatDate, formatCurrency } from '../lib/format';
import { useToast } from '../hooks/use-toast';

const initialForm = { contract_no: '', client_name: '', project_name: '', start_date: '', end_date: '', total_value: 0, status: 'active', vehicle_ids: [], remarks: '' };

const Contracts = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(initialForm);
  const [editing, setEditing] = useState(null);
  const canEdit = hasRole(user, 'super_admin','admin');

  const load = useCallback(async () => { setItems((await api.get('/contracts')).data); setVehicles((await api.get('/vehicles')).data); }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const p = { ...form, total_value: Number(form.total_value) || 0 };
      if (editing) await api.put(`/contracts/${editing.id}`, p);
      else await api.post('/contracts', p);
      toast({ title: editing ? 'Updated' : 'Created' });
      setOpen(false); load();
    } catch (e) { toast({ title: 'Error', description: e.response?.data?.detail, variant: 'destructive' }); }
  };
  const remove = async (c) => { if (!window.confirm('Delete?')) return; await api.delete(`/contracts/${c.id}`); load(); };

  return (
    <div className="space-y-4">
      <PageHeader title="Contracts" description="Manage equipment rental and service contracts."
        actions={canEdit && <Button onClick={() => { setEditing(null); setForm(initialForm); setOpen(true); }} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" />New Contract</Button>} />
      <Card><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 border-b text-left text-slate-600"><tr><th className="p-3">Contract #</th><th className="p-3">Client</th><th className="p-3">Project</th><th className="p-3">Period</th><th className="p-3">Equipment</th><th className="p-3">Value</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th></tr></thead>
          <tbody className="divide-y">
            {items.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-slate-500">No contracts.</td></tr> :
              items.map((c) => (
                <tr key={c.id} className="table-row-hover">
                  <td className="p-3 font-mono font-semibold">{c.contract_no}</td>
                  <td className="p-3">{c.client_name}</td>
                  <td className="p-3">{c.project_name}</td>
                  <td className="p-3 text-xs">{formatDate(c.start_date)} – {formatDate(c.end_date)}</td>
                  <td className="p-3 text-xs">{(c.vehicle_ids || []).length} units</td>
                  <td className="p-3">{formatCurrency(c.total_value)}</td>
                  <td className="p-3"><Badge className={{active:'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',expired:'bg-slate-200 text-slate-700 hover:bg-slate-200',terminated:'bg-red-100 text-red-800 hover:bg-red-100'}[c.status]}>{c.status}</Badge></td>
                  <td className="p-3 text-right">{canEdit && <><Button size="sm" variant="ghost" onClick={() => { setEditing(c); setForm({ ...initialForm, ...c, vehicle_ids: c.vehicle_ids || [] }); setOpen(true); }}><Edit2 className="h-4 w-4" /></Button><Button size="sm" variant="ghost" onClick={() => remove(c)}><Trash2 className="h-4 w-4 text-red-600" /></Button></>}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Contract' : 'New Contract'}</DialogTitle><DialogDescription>Contract terms and assigned equipment.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Contract # *</Label><Input value={form.contract_no} onChange={(e) => setForm({ ...form, contract_no: e.target.value })} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="expired">Expired</SelectItem><SelectItem value="terminated">Terminated</SelectItem></SelectContent></Select>
            </div>
            <div><Label>Client Name *</Label><Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} /></div>
            <div><Label>Project Name</Label><Input value={form.project_name || ''} onChange={(e) => setForm({ ...form, project_name: e.target.value })} /></div>
            <div><Label>Start Date</Label><Input type="date" value={form.start_date || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>End Date</Label><Input type="date" value={form.end_date || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
            <div className="col-span-2"><Label>Total Value (PHP)</Label><Input type="number" value={form.total_value} onChange={(e) => setForm({ ...form, total_value: e.target.value })} /></div>
            <div className="col-span-2"><Label>Assigned Equipment</Label>
              <div className="max-h-40 overflow-y-auto border rounded p-2 space-y-1">
                {vehicles.map((v) => (
                  <label key={v.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 p-1 rounded">
                    <input type="checkbox" checked={form.vehicle_ids.includes(v.id)} onChange={(e) => { const s = new Set(form.vehicle_ids); e.target.checked ? s.add(v.id) : s.delete(v.id); setForm({ ...form, vehicle_ids: Array.from(s) }); }} />
                    <span className="font-mono font-semibold">{v.equipment_code}</span>
                    {v.category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-[hsl(217,91%,24%)] border border-blue-200">{v.category}</span>}
                    <span className="text-slate-500 truncate">– {v.description || ''}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks || ''} onChange={(e) => setForm({ ...form, remarks: e.target.value })} /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={save} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]">{editing ? 'Update' : 'Create'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Contracts;
